import {
  CandidateRow,
  ProjectElementImport,
} from '@shared/models/element-import.model';
import { readKalkylfilRawCell } from '@shared/helpers/kalkylfil-csv.parser';
import type { ImportedElementsByRowId } from './candidate-status.helpers';
import {
  ImportSort,
  loadImportSort,
  resolveImportSort,
} from './import-sort.ls';

export { CSV_IMPORT_EXPAND_PREFIX } from './csv-import.constants';

/**
 * Original CSV order, as parsed at import time. Mirrors the repository's
 * `ORDER BY row_index`, keeping {@link CandidateRowsTable} import sections in
 * the same order as the source file.
 */
export const sortCandidateRows = (rows: CandidateRow[]): CandidateRow[] =>
  [...rows].sort((a, b) => a.rowIndex - b.rowIndex);

/** Human (natural) comparison: "row2" < "row10", numbers before/after text. */
const cellCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

/**
 * Sort candidate rows by one CSV column with human (natural) ordering. Empty
 * cells always sort last regardless of direction; ties fall back to the
 * original CSV order ({@link CandidateRow.rowIndex}) for a stable result.
 */
export const sortCandidateRowsByColumn = (
  rows: CandidateRow[],
  sort: ImportSort,
): CandidateRow[] =>
  [...rows].sort((a, b) => {
    const av = readKalkylfilRawCell(a.raw, sort.column);
    const bv = readKalkylfilRawCell(b.raw, sort.column);
    if (av === bv) return a.rowIndex - b.rowIndex;
    if (av === '') return 1;
    if (bv === '') return -1;
    const cmp = cellCollator.compare(av, bv);
    return sort.dir === 'asc' ? cmp : -cmp;
  });

/** Apply the persisted (and header-validated) sort, else the default order. */
const orderCandidateRows = (
  rows: CandidateRow[],
  imp: ProjectElementImport,
): CandidateRow[] => {
  const sort = resolveImportSort(loadImportSort(imp.id), imp.headers);
  return sort ? sortCandidateRowsByColumn(rows, sort) : sortCandidateRows(rows);
};

/**
 * Imports for the tab, newest first. Scoped to the selected version's
 * `importIds`: candidate rows/imports are stored project-wide, so a version
 * shows only the files it references. A new version does not copy those ids.
 * An undo that removes an `importId` from the version therefore hides that file.
 */
export const getImportsNewestFirst = (
  importsById: Record<string, ProjectElementImport>,
  versionImportIds: readonly string[] | undefined,
): ProjectElementImport[] => {
  const allowed = versionImportIds ? new Set(versionImportIds) : undefined;
  return Object.values(importsById)
    .filter((imp) => !allowed || allowed.has(imp.id))
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
};

/**
 * Import file Cmd+F is scoped to: the file of the selected header/row, else
 * the expanded file, else the newest file. Other files stay on screen unfiltered.
 */
export const resolveSearchScopeImportId = (params: {
  selectedId: string | undefined;
  imports: readonly ProjectElementImport[];
  rowsByImport: Record<string, CandidateRow[]>;
  importedElements: ImportedElementsByRowId;
  isImportExpanded: (importId: string) => boolean;
}): string | undefined => {
  const {
    selectedId,
    imports,
    rowsByImport,
    importedElements,
    isImportExpanded,
  } = params;
  if (imports.length === 0) return undefined;

  if (selectedId && selectedId !== 'none') {
    if (imports.some((imp) => imp.id === selectedId)) return selectedId;
    for (const rows of Object.values(rowsByImport)) {
      const row = rows.find((candidate) => candidate.id === selectedId);
      if (row) return row.importId;
    }
    for (const [rowId, element] of importedElements) {
      if (element.id !== selectedId) continue;
      for (const rows of Object.values(rowsByImport)) {
        const row = rows.find((candidate) => candidate.id === rowId);
        if (row) return row.importId;
      }
    }
  }

  return imports.find((imp) => isImportExpanded(imp.id))?.id ?? imports[0]?.id;
};

type BuildImportTabVisibleRowIdsOptions = {
  visibleImports: ProjectElementImport[];
  rowsByImport: Record<string, CandidateRow[]>;
  searchIsActive: boolean;
  rowMatchesSearch: (row: CandidateRow) => boolean;
  isImportExpanded: (importId: string) => boolean;
  /** When search is active, only this file's rows are filtered / force-opened. */
  searchScopeImportId: string | undefined;
};

/**
 * Flat list of selectable import-list row ids (import headers + candidate rows)
 * in on-screen order for keyboard navigation.
 */
export const buildImportTabVisibleRowIds = ({
  visibleImports,
  rowsByImport,
  searchIsActive,
  rowMatchesSearch,
  isImportExpanded,
  searchScopeImportId,
}: BuildImportTabVisibleRowIdsOptions): string[] => {
  const ids: string[] = [];

  for (const imp of visibleImports) {
    ids.push(imp.id);
    const applySearch = searchIsActive && imp.id === searchScopeImportId;
    const expanded = applySearch || isImportExpanded(imp.id);
    if (!expanded) continue;

    const sourceRows = rowsByImport[imp.id] ?? [];
    const filtered = applySearch
      ? sourceRows.filter(rowMatchesSearch)
      : sourceRows;
    for (const row of orderCandidateRows(filtered, imp)) ids.push(row.id);
  }

  return ids;
};

/**
 * Maps URL selection to a visible import-list row id (candidate row or import
 * header). Imported elements resolve to their source candidate row id.
 * Hidden ids (search/status) are not a selection.
 */
export const resolveImportListNavSelectionId = (
  selectedId: string | undefined,
  visibleIds: readonly string[],
  importedElements: ImportedElementsByRowId,
): string | undefined => {
  if (!selectedId || selectedId === 'none') return undefined;
  if (visibleIds.includes(selectedId)) return selectedId;

  for (const rowId of visibleIds) {
    if (importedElements.get(rowId)?.id === selectedId) return rowId;
  }

  return undefined;
};

export const isImportListGroupRowId = (
  importId: string,
  visibleImports: ProjectElementImport[],
): boolean => visibleImports.some((imp) => imp.id === importId);

/**
 * Drops candidate ids that search/status hid. Import headers stay — they are
 * not in the candidate visible set.
 */
export const restrictImportNavIdsToVisibleCandidates = (
  navIds: readonly string[],
  visibleCandidateIds: ReadonlySet<string>,
  visibleImports: ProjectElementImport[],
): string[] =>
  navIds.filter(
    (id) =>
      isImportListGroupRowId(id, visibleImports) || visibleCandidateIds.has(id),
  );

/**
 * Resolves the candidate row id for discard/restore shortcuts. Import section
 * headers and unknown ids return undefined.
 */
export const resolveImportCandidateRowIdForDiscard = (
  selectedId: string | undefined,
  visibleImports: ProjectElementImport[],
  rowsByImport: Record<string, CandidateRow[]>,
  importedElements: ImportedElementsByRowId,
): string | undefined => {
  if (!selectedId || selectedId === 'none') return undefined;
  if (isImportListGroupRowId(selectedId, visibleImports)) return undefined;

  for (const rows of Object.values(rowsByImport)) {
    if (rows.some((row) => row.id === selectedId)) return selectedId;
  }

  for (const [rowId, element] of importedElements) {
    if (element.id === selectedId) return rowId;
  }

  return undefined;
};
